import { createMemo, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { groupHeadParts, type PagedGroup } from '../lib/fleet-machines';
import type { UtilBox } from '../lib/fleetUtilization';
import { MachineGroupHead } from './MachineGroupHead';

// The Machines list's stretches, by server or as one run (#6560, prototype `cgrid` +
// `groupHead`). Presenter over `pageGroups` (`lib/fleet-machines.ts`): one stretch per
// server when grouped, each under `MachineGroupHead` naming the server (or "Unnamed server,
// <address>"), how many machines it carries and how they are doing.
//
// KEYED, NOT BY OBJECT. Both lists iterate stable keys (a group's server key, a machine's
// id) and look the row up in the current read, so the 20-second poll updates a card in
// place instead of rebuilding it under an open menu or tooltip.
//
// THE BODY IS THE VIEW'S. Cards (the default) hand `body` the Mission Panel card grid
// (`MachineCardGrid`, #6785); the List view (`MachineTable`, #6561) a sortable `DataTable`
// per stretch, and the Map (`MachineMap`, #6562) a run of squares, so all three share one
// grouping, one header and one keyed walk.

const sameKeys = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((k, i) => k === b[i]);

export type MachineGroupsProps = {
  readonly paged: readonly PagedGroup[];
  /**
   * A stretch's body: its machine ids (in order, stable across polls) and the name it is
   * announced by.
   */
  readonly body: (ids: () => readonly string[], label: () => string) => JSX.Element;
  /** The UNFILTERED roster — a group header's server-name write names every machine on the server. */
  readonly allBoxes: readonly UtilBox[];
  readonly token: string | null;
  readonly notify: (message: string, tone?: 'success' | 'error') => void;
  readonly onWritten: () => void;
  readonly canWrite: boolean;
};

export function MachineGroups(props: MachineGroupsProps): JSX.Element {
  const keyOf = (g: PagedGroup): string => g.group?.key ?? '';
  const keys = createMemo(() => props.paged.map(keyOf), undefined, { equals: sameKeys });
  const stretch = (key: string): PagedGroup | undefined =>
    props.paged.find((g) => keyOf(g) === key);
  return (
    <div class="fleet-machines__groups">
      <For each={keys()}>
        {(key) => {
          const ids = createMemo(() => stretch(key)?.rows.map((b) => b.id) ?? [], undefined, {
            equals: sameKeys,
          });
          const label = (): string => {
            const group = stretch(key)?.group;
            return group ? groupHeadParts(group, t).name : t('fleet.machines.heading');
          };
          return (
            <section class="machine-group">
              <Show when={stretch(key)?.group}>
                {(group) => (
                  <MachineGroupHead
                    group={group()}
                    canWrite={props.canWrite}
                    allBoxes={props.allBoxes}
                    token={props.token}
                    notify={props.notify}
                    onWritten={props.onWritten}
                  />
                )}
              </Show>
              {props.body(ids, label)}
            </section>
          );
        }}
      </For>
    </div>
  );
}
